import { allowedFactorRange, isFlatFeeCode, type GoaCode } from "src/helpers/goaRules";
import { activeRows, draftTotals, type GoaLineDraft } from "src/components/GoaLineEditor/goaLineDraft";

// Step 3 (Leistungen) rules. Blocking problems per row mirror what the backend refuses
// (examController: factor range) plus what an invoice under GOÄ §12 needs: a code, a description,
// a fee, and a Begründung above the threshold factor. The same code twice on a day doesn't block —
// it's a finding to confirm in step 4 (goaCheck.duplicateFindings).

export type RowProblem =
    | "codeMissing"
    | "descriptionMissing"
    | "priceMissing"
    | "factorOutOfRange"
    | "justificationMissing"
    | "materialIncomplete";

export type RowWarning = "unknownCode";

/** Services per treatment day (yyyy-mm-dd → rows). Rows of days no longer in the treatment days
 *  are kept (the day may come back) but never count. */
export type ServicesByDay = Record<string, GoaLineDraft[]>;

export function rowProblems(row: GoaLineDraft, catalogByNr: Map<string, GoaCode>): RowProblem[] {
    const problems: RowProblem[] = [];
    const code = row.code.trim();
    if (!code) problems.push("codeMissing");
    if (!row.description.trim()) problems.push("descriptionMissing");
    if (!isFlatFeeCode(code)) {
        if (!(row.price > 0)) problems.push("priceMissing");
        const range = allowedFactorRange(code, catalogByNr);
        if (!(row.factor >= range.min && row.factor <= range.max)) problems.push("factorOutOfRange");
        // Above the threshold (Schwellenwert) GOÄ §12(3) requires a written justification.
        else if (row.factor > range.threshold && !row.justification.trim()) problems.push("justificationMissing");
    }
    if (row.materialLines.some((ml) => !ml.label.trim() || !(ml.cost > 0))) problems.push("materialIncomplete");
    return problems;
}

export function rowWarnings(row: GoaLineDraft, catalogByNr: Map<string, GoaCode>): RowWarning[] {
    const code = row.code.trim();
    // Only once the catalog has loaded — an empty map would flag every code.
    return code && catalogByNr.size > 0 && !isFlatFeeCode(code) && !catalogByNr.has(code) ? ["unknownCode"] : [];
}

export interface ServicesCheck {
    /** Problems per row, by the row's local id. */
    problems: Record<string, RowProblem[]>;
    warnings: Record<string, RowWarning[]>;
    /** Treatment days without a single service. */
    emptyDays: string[];
    rowCount: number;
    totals: { fees: number; material: number; total: number };
    ok: boolean;
}

export function checkServices(services: ServicesByDay, days: string[], catalogByNr: Map<string, GoaCode>): ServicesCheck {
    const problems: Record<string, RowProblem[]> = {};
    const warnings: Record<string, RowWarning[]> = {};
    const emptyDays: string[] = [];
    const counted: GoaLineDraft[] = [];

    for (const day of days) {
        const rows = activeRows(services[day] ?? []);
        if (rows.length === 0) emptyDays.push(day);
        for (const row of rows) {
            counted.push(row);
            const p = rowProblems(row, catalogByNr);
            if (p.length) problems[row._localId] = p;
            const w = rowWarnings(row, catalogByNr);
            if (w.length) warnings[row._localId] = w;
        }
    }

    return {
        problems,
        warnings,
        emptyDays,
        rowCount: counted.length,
        totals: draftTotals(counted),
        ok: days.length > 0 && emptyDays.length === 0 && Object.keys(problems).length === 0,
    };
}
